/**
 * How one locale key reads to a person, in their own UI language.
 *
 * Its own module rather than a function in `locale-helper` because it has no
 * dependencies at all — only `Intl` — while `locale-helper` imports `@cineamo/api`
 * at runtime, and an aliased specifier is exactly what node's type stripping cannot
 * resolve. Splitting it out is what makes it reachable from `node --test`
 * (CIN-14886); `locale-helper` re-exports it, so no call site changes.
 */

/**
 * Splits a locale key into its language and country names, both rendered in
 * `displayLocale` — the language the UI is being viewed in. An admin browsing in
 * English reads "German" / "Switzerland" for 'de-CH'; the same key in the German
 * UI reads "Deutsch" / "Schweiz".
 *
 * Contrast getLocationNameFromLocale, which returns only the country and names it
 * in the language of the key itself rather than the viewer's.
 *
 * `country` is undefined when the key carries no region ('de'). Both parts fall
 * back to the raw subtag when the runtime does not recognize it, so a caller
 * always has something to render.
 *
 * No dependency needed: Intl.DisplayNames draws the ~250 country and ~8000
 * language names from the runtime's CLDR data, already localized — none of it
 * belongs in the translation files.
 *
 * @example
 * localeKey: 'de-CH', displayLocale: 'en-US'
 * returns: { language: 'German', country: 'Switzerland' }
 * @example
 * localeKey: 'de-CH', displayLocale: 'de-DE'
 * returns: { language: 'Deutsch', country: 'Schweiz' }
 */
export function getLocaleKeyDisplayParts(
    localeKey: string,
    displayLocale: string
): { language: string; country: string | undefined } {
    const [languageSubtag, countrySubtag] = localeKey.replace('_', '-').split('-');

    let languageNames: Intl.DisplayNames;
    let countryNames: Intl.DisplayNames;
    try {
        // 'standard' avoids dialect names ("Swiss High German") for regional keys
        languageNames = new Intl.DisplayNames([displayLocale], {
            type: 'language',
            languageDisplay: 'standard'
        });
        countryNames = new Intl.DisplayNames([displayLocale], { type: 'region' });
    } catch {
        // A structurally invalid displayLocale makes the constructor throw
        return { language: languageSubtag, country: countrySubtag };
    }

    return {
        language: languageNames.of(languageSubtag) ?? languageSubtag,
        country: countrySubtag ? (countryNames.of(countrySubtag) ?? countrySubtag) : undefined
    };
}
